import { Box } from '@factorialco/deprecated-design-system'
import { CoveragesByDayDocument, CoveragesByHourDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useLazyQuery } from 'modules/core/hooks/useQuery'

import CoverageShiftsSummary from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/Body/CoverageShiftsSummary'
import {
  Column,
  ColumnsType,
} from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/buildColumns'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type { Shift } from 'modules/shift_management/lib/types'
import Coverages from 'modules/shift_management/store/internal/collections/Coverages'
import CoveragesByDay from 'modules/shift_management/store/internal/collections/CoveragesByDay'
import { overrideGammaDimensions } from 'modules/shift_management/store/internal/models/View'

interface Props {
  columns: ColumnsType
  coverageShifts: Shift[]
}

const CoverageSummary = ({ columns, coverageShifts }: Props) => {
  const [{ view, newShiftsToQuery }] = useSchedule()
  const columnMinWidth = view.columnMinWidth
  const dimensions = overrideGammaDimensions()
  const width = dimensions[columnMinWidth]
  const [filters] = useFilters()

  const { company } = useCurrentSession()
  const locationIds = filters.locationIds

  const [getCoverageCountersByDay, { data: coverageCountersByDayData }] =
    useLazyQuery(CoveragesByDayDocument)
  const [getCoverageCountersByHour, { data: coverageCountersByHourData }] =
    useLazyQuery(CoveragesByHourDocument)

  useUnsafeEffect(() => {
    if (view.isWeekly) return
    const commonVariables = {
      startAt: view.startAt,
      endAt: view.endAt,
      customFilters: filters.customFilters,
      employeeIds: filters.employeeIds ?? undefined,
      logicOperator: filters.logicOperator,
      locationIds: locationIds ?? undefined,
      shiftIds: filters.shiftIds,
      teamIds: filters.teamIds ?? undefined,
      terminated: filters.terminated,
    }
    if (view.isDaily) {
      getCoverageCountersByHour({
        variables: {
          ...commonVariables,
          usePercentages: false,
        },
      })
    } else {
      getCoverageCountersByDay({
        variables: {
          companyId: company.id,
          ...commonVariables,
        },
      })
    }
  }, [
    view,
    newShiftsToQuery,
    filters,
    locationIds,
    company.id,
    getCoverageCountersByDay,
    getCoverageCountersByHour,
  ])

  const peopleData = useUnsafeMemo(() => {
    return view.isDaily
      ? Coverages.buildFromRequest(
          coverageCountersByHourData?.shiftManagement.coveragesConnection.nodes ?? []
        )
      : CoveragesByDay.buildFromRequest(
          coverageCountersByDayData?.shiftManagement.coveragesByDaysConnection.nodes ?? []
        )
  }, [coverageCountersByDayData, coverageCountersByHourData, view])

  return (
    <Box
      style={{
        gridTemplateColumns: `repeat(${view.dates.length}, minmax(${width}, 1fr))`,
      }}
      flexDirection="row"
      height="full"
    >
      {columns.map(
        (heading: Column, index: number) =>
          index !== 0 && (
            <CoverageShiftsSummary
              key={index}
              date={heading.date}
              coverageShifts={coverageShifts}
              peopleData={peopleData}
            />
          )
      )}
    </Box>
  )
}

export default CoverageSummary
